/**
 * WHERE THE FOCUS GOES AFTER A MOVE between places, once, for every place, so a screen reader says
 * where she has arrived. A tap on a tab, a face, "See this invoice" or any other link inside the app
 * draws another page in place of the one she was on, and the link that had the focus goes with it:
 * left alone, the focus is nowhere, and a screen reader says nothing of the new page.
 *
 * - AFTER EVERY MOVE SHE MAKES, the focus goes to the new page's main heading, its `h1`, once the
 *   page has drawn it, which for a place is once its read has answered. The heading is made
 *   focusable by a script alone (`tabindex="-1"`), so the Tab key never stops on it, and it is drawn
 *   with the design's focus ring and nothing of its own, as every element is (`styles.css`).
 * - NEVER ON THE FIRST LOAD. The page a person opens is read from its top, as any page is. A load
 *   that sends itself on to another place, such as an old link landing on Home, is the first load
 *   still: a move counts only once she has pressed, tapped or typed on the page.
 * - NEVER OVER A FOCUS A SCREEN PLACES ITSELF. Sign-in puts the cursor in the field to type in, and
 *   Messages opened by "Ask us for something" puts it in the composer; whatever a screen, or she
 *   herself, gives the focus to before the heading would take it keeps it. The heading waits for a
 *   turn of the page's clock after it is drawn, so a screen's own focus, given as it is drawn, comes
 *   first. Sheets, the steps inside a place and its lines that say sent are not moves between
 *   places, and place the focus as they always have.
 * - A PLACE WHOSE READ FAILED IS A PAGE TOO: it draws its own heading over the line that says so
 *   (`Guarded.tsx`), and the focus lands there as on any other.
 * - THE DOOR IN PLACE OF A SIGNED-IN SCREEN, with no move: a sign-out, or a 401 in the middle of a
 *   session, draws the door where she was and leaves the route as it was, so nothing here sees a
 *   move. The door takes the focus to its heading itself, by the same rules (`useHeadingFocus`),
 *   whenever it replaces a screen she was on, and never on a first load. So does the page that
 *   says to call, drawn where she was when a place read again answers 404, which is how a
 *   membership ops revokes in the middle of a session reads.
 * - A SHEET WHOSE OPENER HAS GONE: a sheet closed after a read since took away the button that
 *   opened it, and anything near it the place would give the focus to, gives the page's main
 *   heading the focus by the same rule (`focusPageHeading`), so the focus is never left on nothing.
 *
 * Drawn once, beside the routes, in `App.tsx`.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';

/** What says a person has acted on the page: a press, a tap, a click or a key. */
const ACTS = ['pointerdown', 'mousedown', 'click', 'keydown'] as const;

/** The new page's main heading. */
const HEADING = 'main h1';

/** The page's main heading, where it is drawn. */
const mainHeading = () => document.querySelector<HTMLElement>(HEADING);

/**
 * Gives the page's main heading the focus at once, made focusable by a script alone as after a
 * move: for a sheet that closes once what opened it has gone, as a sheet on a visit, a home, a
 * person or a method a read since no longer holds does after a 404 it said (the owner, 2026-10-20),
 * so the focus is never left nowhere. Nothing where the page draws no such heading.
 */
export function focusPageHeading(): void {
  const heading = mainHeading();
  if (heading === null) return;
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  heading.focus();
}

/**
 * Waits for the page's heading and gives it the focus, unless the focus has gone somewhere since
 * the move. `left` is what had the focus as the move was drawn, which is nowhere unless it is still
 * on the page. `find` answers the heading, the page's main one unless a screen names its own.
 * Answers the way to stop waiting.
 */
function focusHeadingWhenDrawn(
  left: Element | null,
  find: () => HTMLElement | null = mainHeading,
): () => void {
  let waiting: ReturnType<typeof setTimeout> | undefined;
  let done = false;

  /** Whether the focus is still where the move left it: nowhere, or on what had it then. */
  const unplaced = () => {
    const at = document.activeElement;
    return at === null || at === document.body || at === left || !at.isConnected;
  };

  const finish = () => {
    done = true;
    observer.disconnect();
    if (waiting !== undefined) clearTimeout(waiting);
  };

  const look = () => {
    if (done || waiting !== undefined) return;
    if (!unplaced()) return finish();
    const heading = find();
    if (heading === null) return;
    // A turn of the clock, so a focus the screen gives as it is drawn is given first.
    waiting = setTimeout(() => {
      waiting = undefined;
      if (!unplaced()) return finish();
      if (!heading.isConnected) return look();
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus();
      finish();
    }, 0);
  };

  const observer = new MutationObserver(look);
  observer.observe(document.body, { childList: true, subtree: true });
  look();
  return finish;
}

/**
 * For a page drawn in place of another with no move between places, such as the door after a
 * sign-out: once `take` is true, the focus goes to the page's main heading when it is drawn, by the
 * same rules as after a move, never over a focus given since. `take` is false on a first load, where
 * nothing moves. `heading`, where given, is the heading that says what has come, where that is not
 * the page's main one: an invoice that came while its page said it was on its way, drawn under the
 * words back from Stripe's page, whose heading is the page's (`screens/Invoice.tsx`, the review of
 * fe216b8, finding 3).
 */
export function useHeadingFocus(take: boolean, heading?: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!take) return;
    return focusHeadingWhenDrawn(
      document.activeElement,
      heading === undefined ? undefined : () => heading.current,
    );
  }, [take]);
}

export function RouteFocus() {
  const location = useLocation();
  /** The move drawn last, so the same one drawn again is not a move. */
  const drawn = useRef(location.key);
  /** Whether a person has acted on the page since it loaded. */
  const acted = useRef(false);

  useEffect(() => {
    const act = () => {
      acted.current = true;
    };
    for (const kind of ACTS) document.addEventListener(kind, act, true);
    return () => {
      for (const kind of ACTS) document.removeEventListener(kind, act, true);
    };
  }, []);

  useEffect(() => {
    if (location.key === drawn.current) return;
    drawn.current = location.key;
    if (!acted.current) return;
    return focusHeadingWhenDrawn(document.activeElement);
  }, [location.key]);

  return null;
}
